import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import DataTable from "../components/ui/data-table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { 
  Loader2, Plus, Pencil, Trash2, Users, FileText, TrendingDown,
  Euro, Calendar, Building, FolderPlus, Settings, BarChart3
} from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";
import { useTranslation } from "../contexts/I18nContext";

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT');
  } catch {
    return dateStr;
  }
};

export default function CostiExtraPage() {
  const { t } = useTranslation();
  const MESI = [
    { value: '01', label: t('months.january') },
    { value: '02', label: t('months.february') },
    { value: '03', label: t('months.march') },
    { value: '04', label: t('months.april') },
    { value: '05', label: t('months.may') },
    { value: '06', label: t('months.june') },
    { value: '07', label: t('months.july') },
    { value: '08', label: t('months.august') },
    { value: '09', label: t('months.september') },
    { value: '10', label: t('months.october') },
    { value: '11', label: t('months.november') },
    { value: '12', label: t('months.december') },
  ];
  const { canEdit } = usePermissions();
  const canEditCosti = canEdit("costi_extra");
  
  const [activeTab, setActiveTab] = useState("personale");
  const [loading, setLoading] = useState(true);
  const [costs, setCosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Filtri
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState("");
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Category dialog
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  
  // Form state
  const [form, setForm] = useState({
    descrizione: "",
    importo: "",
    data: new Date().toISOString().split('T')[0],
    mese_riferimento: "",
    account_id: "",
    cost_center_id: "",
    note: "",
    categoria_id: "",
    // Per ammortamenti manuali
    valore_bene: "",
    quota_annuale_percentuale: "",
    data_inizio_ammortamento: ""
  });
  
  const [categoryForm, setCategoryForm] = useState({
    codice: "",
    nome: "",
    descrizione: "",
    tipo: "altro"
  });

  const fetchData = useCallback(async () => {
    try {
      const prefix = getApiPrefix();
      
      // Costruisci parametri per i costi
      let costsParams = `?anno=${selectedYear}`;
      if (activeTab !== "categorie" && activeTab !== "statistiche") {
        costsParams += `&categoria_tipo=${activeTab}`;
      }
      if (selectedMonth) {
        costsParams += `&mese_riferimento=${selectedYear}-${selectedMonth}`;
      }
      
      const [costsRes, categoriesRes, accountsRes, ccRes, statsRes] = await Promise.all([
        api.get(`${prefix}/other-costs${costsParams}`),
        api.get(`${prefix}/cost-categories`),
        api.get(`${prefix}/accounts`).catch(() => ({ data: [] })),
        api.get(`${prefix}/cost-centers`).catch(() => ({ data: [] })),
        api.get(`${prefix}/other-costs/stats?anno=${selectedYear}`).catch(() => ({ data: null }))
      ]);
      
      setCosts(costsRes.data);
      setCategories(categoriesRes.data);
      setAccounts(accountsRes.data || []);
      setCostCenters(ccRes.data || []);
      setStats(statsRes.data);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
      
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calcola statistiche per tab attiva
  const tabStats = useMemo(() => {
    const filtered = costs.filter(c => c.categoria_tipo === activeTab);
    return {
      count: filtered.length,
      totale: filtered.reduce((sum, c) => sum + (parseFloat(c.importo) || 0), 0)
    };
  }, [costs, activeTab]);

  // Handler per creare/modificare costo
  const openCostDialog = (item = null, tabType = null) => {
    if (item) {
      setEditingItem(item);
      setForm({
        descrizione: item.descrizione || "",
        importo: item.importo?.toString() || "",
        data: item.data?.split('T')[0] || new Date().toISOString().split('T')[0],
        mese_riferimento: item.mese_riferimento || "",
        account_id: item.account_id || "",
        cost_center_id: item.cost_center_id || "",
        note: item.note || "",
        categoria_id: item.categoria_id || "",
        valore_bene: item.valore_bene?.toString() || "",
        quota_annuale_percentuale: item.quota_annuale_percentuale?.toString() || "",
        data_inizio_ammortamento: item.data_inizio_ammortamento?.split('T')[0] || ""
      });
    } else {
      setEditingItem(null);
      const defaultCategory = categories.find(c => c.tipo === (tabType || activeTab));
      const today = new Date();
      setForm({
        descrizione: "",
        importo: "",
        data: today.toISOString().split('T')[0],
        mese_riferimento: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`,
        account_id: "",
        cost_center_id: "",
        note: "",
        categoria_id: defaultCategory?.id || "",
        valore_bene: "",
        quota_annuale_percentuale: "",
        data_inizio_ammortamento: ""
      });
    }
    setDialogOpen(true);
  };

  const handleCostSubmit = async () => {
    if (!form.descrizione || !form.importo) {
      handleApiError({ message: t("errors.requiredDescription") });
      return;
    }
    
    setSaving(true);
    try {
      const prefix = getApiPrefix();
      const payload = {
        ...form,
        importo: parseFloat(form.importo),
        valore_bene: form.valore_bene ? parseFloat(form.valore_bene) : null,
        quota_annuale_percentuale: form.quota_annuale_percentuale ? parseFloat(form.quota_annuale_percentuale) : null,
        account_id: form.account_id || null,
        cost_center_id: form.cost_center_id || null
      };
      
      if (editingItem) {
        await api.put(`${prefix}/other-costs/${editingItem.id}`, payload);
        showSuccess(t("success.costUpdated"));
      } else {
        await api.post(`${prefix}/other-costs`, payload);
        showSuccess(t("success.costCreated"));
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`${getApiPrefix()}/other-costs/${deleteDialog.item.id}`);
      showSuccess(t("success.costDeleted"));
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  const handleBulkDelete = async (ids) => {
    let deleted = 0;
    const prefix = getApiPrefix();
    for (const id of ids) {
      try {
        await api.delete(`${prefix}/other-costs/${id}`);
        deleted++;
      } catch (err) {
        
      }
    }
    showSuccess(`${deleted} costi eliminati`);
    fetchData();
  };

  // Handler per categorie
  const openCategoryDialog = (item = null) => {
    if (item) {
      setEditingCategory(item);
      setCategoryForm({
        codice: item.codice || "",
        nome: item.nome || "",
        descrizione: item.descrizione || "",
        tipo: item.tipo || "altro"
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        codice: "",
        nome: "",
        descrizione: "",
        tipo: "altro"
      });
    }
    setCategoryDialogOpen(true);
  };

  const handleCategorySubmit = async () => {
    if (!categoryForm.codice || !categoryForm.nome) {
      handleApiError({ message: t("errors.requiredCodeName") });
      return;
    }
    
    setSaving(true);
    try {
      const prefix = getApiPrefix();
      
      if (editingCategory) {
        await api.put(`${prefix}/cost-categories/${editingCategory.id}`, categoryForm);
        showSuccess(t("success.categoryUpdated"));
      } else {
        await api.post(`${prefix}/cost-categories`, categoryForm);
        showSuccess(t("success.categoryCreated"));
      }
      
      setCategoryDialogOpen(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryDelete = async (id) => {
    if (!window.confirm(t("confirm.deleteCategory"))) return;
    try {
      await api.delete(`${getApiPrefix()}/cost-categories/${id}`);
      showSuccess(t("success.categoryDeleted"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  // Colonne per la tabella costi
  const costsColumns = useMemo(() => [
    {
      key: 'mese_riferimento',
      label: 'Mese',
      width: '100px',
      render: (value) => {
        if (!value) return "-";
        const [year, month] = value.split('-');
        const meseLabel = MESI.find(m => m.value === month)?.label || month;
        return `${meseLabel} ${year}`;
      }
    },
    {
      key: 'descrizione',
      label: t('common.description'),
      render: (value) => <span className="font-medium">{value}</span>
    },
    {
      key: 'importo',
      label: t('common.amount'),
      align: 'right',
      render: (value) => <span className="font-mono text-amber-600">{formatCurrency(value)}</span>
    },
    {
      key: 'account_nome',
      label: t('movements.account'),
      render: (value, row) => row.account_codice ? 
        <Badge variant="outline">[{row.account_codice}] {value}</Badge> : 
        <span className="text-muted-foreground">-</span>
    },
    {
      key: 'cdc_nome',
      label: 'CDC',
      render: (value, row) => row.cdc_codice ? 
        <Badge variant="secondary">{row.cdc_codice}</Badge> : 
        <span className="text-muted-foreground">-</span>
    },
    {
      key: 'data',
      label: t('common.date'),
      width: '100px',
      render: (value) => formatDate(value)
    },
    {
      key: 'actions',
      label: t('common.actions'),
      align: 'right',
      width: '100px',
      sortable: false,
      filterable: false,
      exportable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => openCostDialog(row)} data-testid={`edit-cost-${row.id}`}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDeleteDialog({ open: true, item: row })} className="text-destructive" data-testid={`delete-cost-${row.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ], []);

  // Colonne per categorie
  const categoryColumns = [
    { key: 'codice', label: t('common.code'), width: '100px' },
    { key: 'nome', label: t('common.name') },
    { 
      key: 'tipo', 
      label: t('common.type'),
      render: (value) => {
        const colors = {
          personale: 'bg-blue-100 text-blue-800',
          ratei: 'bg-purple-100 text-purple-800',
          ammortamenti: 'bg-amber-100 text-amber-800',
          altro: 'bg-gray-100 text-gray-800'
        };
        return <Badge className={colors[value] || colors.altro}>{value}</Badge>;
      }
    },
    { key: 'descrizione', label: t('common.description') },
    {
      key: 'is_system',
      label: 'Sistema',
      width: '80px',
      render: (value) => value ? <Badge variant="secondary">Sistema</Badge> : null
    },
    {
      key: 'actions',
      label: t('common.actions'),
      align: 'right',
      width: '100px',
      sortable: false,
      filterable: false,
      render: (_, row) => !row.is_system ? (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => openCategoryDialog(row)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleCategoryDelete(row.id)} className="text-destructive">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ) : null
    }
  ];

  // Anni disponibili per filtro
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - i);
  }, []);

  // Filtra costi per tab attiva
  const filteredCosts = useMemo(() => {
    if (activeTab === "categorie" || activeTab === "statistiche") return [];
    return costs.filter(c => c.categoria_tipo === activeTab);
  }, [costs, activeTab]);

  // Render statistiche
  const renderStats = () => {
    if (!stats) return null;
    
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Riepilogo Anno {selectedYear}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary mb-4">
              {formatCurrency(stats.totale_generale)}
            </div>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Per Categoria</h4>
                <div className="space-y-2">
                  {stats.by_category?.map((cat, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{cat.tipo}</Badge>
                        <span>{cat.categoria_nome}</span>
                        <span className="text-muted-foreground text-sm">({cat.count})</span>
                      </div>
                      <span className="font-mono">{formatCurrency(cat.totale)}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {stats.by_cdc?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Per Centro di Costo</h4>
                  <div className="space-y-2">
                    {stats.by_cdc.map((cdc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{cdc.cdc_codice}</Badge>
                          <span>{cdc.cdc_nome}</span>
                        </div>
                        <span className="font-mono">{formatCurrency(cdc.totale)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {stats.by_month?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">{t('common.monthlyTrend')}</h4>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    {stats.by_month.map((m, idx) => {
                      const [, month] = m.mese.split('-');
                      const meseLabel = MESI.find(me => me.value === month)?.label?.substring(0, 3) || month;
                      return (
                        <div key={idx} className="text-center p-2 bg-muted/50 rounded">
                          <div className="text-xs text-muted-foreground">{meseLabel}</div>
                          <div className="font-mono text-sm">{formatCurrency(m.totale)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="costi-extra-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("extraCosts.title")}</h1>
          <p className="text-muted-foreground">Gestione costi non legati a fatture</p>
        </div>
        
        {/* Filtri anno/mese */}
        <div className="flex items-center gap-2">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-24" data-testid="select-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={selectedMonth || "__all__"} onValueChange={(v) => setSelectedMonth(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-32" data-testid="select-month">
              <SelectValue placeholder={t("common.allMonths")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t('common.allMonths')}</SelectItem>
              {MESI.map(m => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="personale" className="flex items-center gap-2" data-testid="tab-personale">
            <Users className="h-4 w-4" />{t('common.personnel')}</TabsTrigger>
          <TabsTrigger value="ratei" className="flex items-center gap-2" data-testid="tab-ratei">
            <FileText className="h-4 w-4" />
            Ratei
          </TabsTrigger>
          <TabsTrigger value="ammortamenti" className="flex items-center gap-2" data-testid="tab-ammortamenti">
            <TrendingDown className="h-4 w-4" />
            Ammortamenti
          </TabsTrigger>
          <TabsTrigger value="categorie" className="flex items-center gap-2" data-testid="tab-categorie">
            <Settings className="h-4 w-4" />{t('common.categories')}</TabsTrigger>
          <TabsTrigger value="statistiche" className="flex items-center gap-2" data-testid="tab-statistiche">
            <BarChart3 className="h-4 w-4" />{t('common.statistics')}</TabsTrigger>
        </TabsList>

        {/* Tab Personale */}
        <TabsContent value="personale">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-500" />
                  Costi del Personale
                </CardTitle>
                <CardDescription>Stipendi, contributi e costi correlati al personale (aggregati mensili)</CardDescription>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">{t("common.totalPeriod")}</div>
                  <div className="text-2xl font-bold text-blue-600">{formatCurrency(tabStats.totale)}</div>
                </div>
                <Button onClick={() => openCostDialog(null, 'personale')} data-testid="add-personnel-cost">
                  <Plus className="h-4 w-4 mr-2" /> Aggiungi Costo
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={costsColumns}
                data={filteredCosts}
                onDelete={handleBulkDelete}
                idField="id"
                emptyMessage="Nessun costo del personale registrato"
                exportFileName="costi_personale"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Ratei */}
        <TabsContent value="ratei">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-500" />
                  Ratei e Risconti
                </CardTitle>
                <CardDescription>Ratei attivi e passivi</CardDescription>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">{t("common.totalPeriod")}</div>
                  <div className="text-2xl font-bold text-purple-600">{formatCurrency(tabStats.totale)}</div>
                </div>
                <Button onClick={() => openCostDialog(null, 'ratei')} data-testid="add-ratei">
                  <Plus className="h-4 w-4 mr-2" /> Aggiungi Rateo
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={costsColumns}
                data={filteredCosts}
                onDelete={handleBulkDelete}
                idField="id"
                emptyMessage="Nessun rateo registrato"
                exportFileName="ratei"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Ammortamenti Manuali */}
        <TabsContent value="ammortamenti">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-amber-500" />
                  Ammortamenti Manuali
                </CardTitle>
                <CardDescription>Ammortamenti non derivanti da fatture (es. anni precedenti)</CardDescription>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">{t("common.totalPeriod")}</div>
                  <div className="text-2xl font-bold text-amber-600">{formatCurrency(tabStats.totale)}</div>
                </div>
                <Button onClick={() => openCostDialog(null, 'ammortamenti')} data-testid="add-ammortamento">
                  <Plus className="h-4 w-4 mr-2" /> Aggiungi Ammortamento
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={costsColumns}
                data={filteredCosts}
                onDelete={handleBulkDelete}
                idField="id"
                emptyMessage="Nessun ammortamento manuale registrato"
                exportFileName="ammortamenti_manuali"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Categorie */}
        <TabsContent value="categorie">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Categorie Costi
                </CardTitle>
                <CardDescription>Gestisci le categorie dei costi extra-fattura</CardDescription>
              </div>
              <Button onClick={() => openCategoryDialog()} data-testid="add-category">
                <FolderPlus className="h-4 w-4 mr-2" /> {t("categories.newCategory")}
              </Button>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={categoryColumns}
                data={categories}
                idField="id"
                emptyMessage="Nessuna categoria trovata"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Statistiche */}
        <TabsContent value="statistiche">
          {renderStats()}
        </TabsContent>
      </Tabs>

      {/* Dialog Costo */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? t("extraCosts.editCost") : t("extraCosts.newCost")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <SearchableSelect
                options={categories.map(c => ({ value: c.id, label: `[${c.codice}] ${c.nome}` }))}
                value={form.categoria_id}
                onValueChange={(v) => setForm({ ...form, categoria_id: v })}
                placeholder={t("common.selectPlaceholder")}
                searchPlaceholder={t("common.searchPlaceholder")}
                testId="select-categoria"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Descrizione *</Label>
              <Input
                value={form.descrizione}
                onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                placeholder="Es: Stipendi Gennaio 2025"
                data-testid="input-descrizione"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.amount')} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.importo}
                  onChange={(e) => setForm({ ...form, importo: e.target.value })}
                  placeholder="0.00"
                  data-testid="input-importo"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("common.date")}</Label>
                <Input
                  type="date"
                  value={form.data}
                  onChange={(e) => setForm({ ...form, data: e.target.value })}
                  data-testid="input-data"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Mese di Riferimento</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select 
                  value={form.mese_riferimento?.split('-')[0] || selectedYear.toString()}
                  onValueChange={(y) => {
                    const month = form.mese_riferimento?.split('-')[1] || '01';
                    setForm({ ...form, mese_riferimento: `${y}-${month}` });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("common.year")} />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={form.mese_riferimento?.split('-')[1] || ''}
                  onValueChange={(m) => {
                    const year = form.mese_riferimento?.split('-')[0] || selectedYear;
                    setForm({ ...form, mese_riferimento: `${year}-${m}` });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("common.month")} />
                  </SelectTrigger>
                  <SelectContent>
                    {MESI.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t('movements.account')}</Label>
              <SearchableSelect
                options={[
                  { value: "__none__", label: "Nessuno" },
                  ...accounts.map(a => ({ value: a.id, label: `[${a.codice}] ${a.nome}` }))
                ]}
                value={form.account_id}
                onValueChange={(v) => setForm({ ...form, account_id: v })}
                placeholder={t("common.selectPlaceholder")}
                searchPlaceholder={t("common.searchPlaceholder")}
                testId="select-account"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('costCenters.title')}</Label>
              <SearchableSelect
                options={[
                  { value: "__none__", label: "Nessuno" },
                  ...costCenters.map(cc => ({ value: cc.id, label: `[${cc.codice}] ${cc.nome}` }))
                ]}
                value={form.cost_center_id}
                onValueChange={(v) => setForm({ ...form, cost_center_id: v })}
                placeholder={t("common.selectPlaceholder")}
                searchPlaceholder={t("common.searchPlaceholder")}
                testId="select-cdc"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder={t("common.notes")}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleCostSubmit} disabled={saving} data-testid="save-cost-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingItem ? "Salva" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Categoria */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? t("categories.editCategory") : t("categories.newCategory")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.code')} *</Label>
                <Input
                  value={categoryForm.codice}
                  onChange={(e) => setCategoryForm({ ...categoryForm, codice: e.target.value.toUpperCase() })}
                  placeholder="ES: CONS"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("common.type")}</Label>
                <Select value={categoryForm.tipo} onValueChange={(v) => setCategoryForm({ ...categoryForm, tipo: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personale">{t('common.personnel')}</SelectItem>
                    <SelectItem value="ratei">Ratei</SelectItem>
                    <SelectItem value="ammortamenti">Ammortamenti</SelectItem>
                    <SelectItem value="altro">{t('common.other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('common.name')} *</Label>
              <Input
                value={categoryForm.nome}
                onChange={(e) => setCategoryForm({ ...categoryForm, nome: e.target.value })}
                placeholder="Nome categoria"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("common.description")}</Label>
              <Textarea
                value={categoryForm.descrizione}
                onChange={(e) => setCategoryForm({ ...categoryForm, descrizione: e.target.value })}
                placeholder={t("common.optional")}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleCategorySubmit} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingCategory ? "Salva" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
            <DialogDescription>
              Eliminare il costo "{deleteDialog.item?.descrizione}"? Questa azione è irreversibile.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, item: null })}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">{t("common.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
